import type { JobFileEntry } from "@roof-hawk/core";
import { useRef, useState } from "react";

import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import {
  fetchExpenseReceipts,
  signJobFile,
  uploadExpenseReceipt,
} from "../../lib/queries";

/**
 * Receipts on an other expense.
 *
 * Jonathon, 2026-10-23: "please add a feature to upload a receipt to an other
 * expense. when uploaded you can view it there and it will also go to docs for
 * that job"
 *
 * One `job_files` row per receipt, read two ways: by expense here, and by job
 * on the Docs card. Nothing is copied, so the two can never disagree.
 */

/** Every typed expense's receipts, in one read, and the upload that adds one. */
export function useExpenseReceipts(
  jobId: string,
  expenseIds: readonly string[],
  profileId: string | null,
): {
  byExpense: ReadonlyMap<string, readonly JobFileEntry[]>;
  upload: (
    expense: { id: string; name: string | null },
    file: File,
  ) => Promise<void>;
} {
  const key = expenseIds.join(",");
  const receipts = useAsync(() => fetchExpenseReceipts(expenseIds), [key]);

  const byExpense = new Map<string, JobFileEntry[]>();
  for (const row of receipts.data ?? []) {
    const list = byExpense.get(row.job_expense_id) ?? [];
    list.push(row);
    byExpense.set(row.job_expense_id, list);
  }

  async function upload(
    expense: { id: string; name: string | null },
    file: File,
  ) {
    await uploadExpenseReceipt({
      jobId,
      expenseId: expense.id,
      expenseName: expense.name,
      file,
      profileId,
    });
    await receipts.reload();
  }

  return { byExpense, upload };
}

/**
 * The receipt buttons on one expense row: each receipt filed on it, and one
 * to add another.
 *
 * Photos as well as PDFs — a receipt is as often a picture taken at the
 * counter as a file in an inbox, and on a phone `image/*` offers the camera.
 */
export function ExpenseReceiptButtons({
  receipts,
  onUpload,
}: {
  receipts: readonly JobFileEntry[];
  onUpload: (file: File) => Promise<void>;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function open(entry: JobFileEntry) {
    // Signed on demand: the bucket is private, and a link goes stale.
    window.open(await signJobFile(entry), "_blank", "noopener");
  }

  async function chosen(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await onUpload(file);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      // Cleared so choosing the same file again still fires a change.
      if (input.current) input.current.value = "";
    }
  }

  return (
    <>
      {receipts.map((entry, i) => (
        <button
          key={entry.id}
          className="btn-small"
          title={t("Open {title}", { title: entry.title })}
          onClick={() => void open(entry)}
        >
          📎{" "}
          {receipts.length === 1
            ? t("Receipt")
            : t("Receipt {n}", { n: i + 1 })}
        </button>
      ))}
      <button
        className="btn-small"
        disabled={busy}
        title={t(
          "Upload a receipt for this expense. It is kept here and on the job's Docs card.",
        )}
        onClick={() => input.current?.click()}
      >
        {busy
          ? t("Uploading…")
          : receipts.length
            ? t("+ Receipt")
            : t("Add receipt")}
      </button>
      <input
        ref={input}
        type="file"
        accept="image/*,application/pdf"
        hidden
        aria-label={t("Receipt file")}
        onChange={(e) => void chosen(e.target.files?.[0])}
      />
      {error ? <span className="autowarn">{error}</span> : null}
    </>
  );
}
